import { useEffect, useState, type FormEvent } from "react";
import type { StaffMember, StaffRosterResponse } from "../../shared/dto";
import type { StaffPaneProps } from "./console-types";

/**
 * Tomas Pohl 2026-09-17: "hrozne dlouho mi trvalo, nez jsem nasel, kde
 * v profilu klienta priradim partaka."
 *
 * Until now the ONLY place inside a client's page that could assign a partner
 * was the whole-row editor at the bottom of Nastaveni, nine fields deep, where
 * the partner select sits between the journey axis and the owner id. This is
 * the one control that does one thing: it stands at the top of Nastaveni, in
 * the card that already names the partner, and in the right column a link
 * points at it.
 *
 * It writes through the NARROW route (`PATCH .../partner`), never the editor's
 * blind whole-row write (the S3-01 law): the queue and this control therefore
 * share one server path, one audit event and one rule about who is told.
 */
export function PartnerAssign(props: Pick<StaffPaneProps, "detail" | "t" | "run" | "refresh">) {
  const current = props.detail.client.partner?.id ?? "";
  const [members, setMembers] = useState<StaffMember[]>([]);
  const [picked, setPicked] = useState(current);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setPicked(current);
  }, [current]);

  useEffect(() => {
    void (async () => {
      try {
        const response = await fetch("/api/admin/staff", { credentials: "include" });
        if (!response.ok) return;
        const body = await response.json() as StaffRosterResponse;
        setMembers(body.members.filter((member) => member.isActive));
      } catch {
        // The select then offers only the current partner; the save still works.
      }
    })();
  }, []);

  const options = props.detail.client.partner && !members.some((member) => member.id === current)
    ? [{ id: current, displayName: props.detail.client.partner.name, email: props.detail.client.partner.name ?? current } as StaffMember, ...members]
    : members;

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!picked || picked === current) return;
    setBusy(true);
    setFailed(false);
    const response = await props.run(
      `/api/admin/clients/${encodeURIComponent(props.detail.client.id)}/partner`,
      { method: "PATCH", body: { partnerId: picked } },
      { refresh: false }
    );
    setBusy(false);
    if (!response.ok) {
      setFailed(true);
      return;
    }
    await props.refresh();
  }

  return (
    <form className="partner-assign" onSubmit={save} data-admin-tool="assign-partner">
      <label className="inline-select">
        <span>{props.t("staffConsole.assignment.select")}</span>
        <select value={picked} onChange={(event) => setPicked(event.target.value)} disabled={busy}>
          <option value="">{props.t("staffConsole.assignment.pick")}</option>
          {options.map((member) => (
            <option key={member.id} value={member.id}>{member.displayName ?? member.email}</option>
          ))}
        </select>
      </label>
      <div className="button-row">
        <button className="primary-button" type="submit" disabled={busy || !picked || picked === current}>
          {busy
            ? props.t("staffConsole.assignment.saving")
            : props.t(current ? "staffConsole.assignment.change" : "staffConsole.assignment.assign")}
        </button>
      </div>
      <p className="staff-card-hint">{props.t("staffConsole.assignment.hint")}</p>
      {failed && <p className="message error-message" role="alert">{props.t("staffConsole.assignment.failed")}</p>}
    </form>
  );
}
